import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { style } from "./style";

import { blockedBookingAvailabilitySettingApi } from "@/utils/apis/doctor/availabilitySetting";
import { capitalizeFirstLetter } from "@/utils";

import {
  availabilitySettings,
  setBlockedBookingAvailabilitySetting,
} from "@/store/slices/doctor/availabilitySettings";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { IRoleType } from "@/models/role";

const names = [
  "ALL",
  IRoleType.PATIENT,
  IRoleType.DOCTOR,
  IRoleType.PHARMACY,
  IRoleType.ADMIN,
];

const blockedBookingNameList = names.map((name) => capitalizeFirstLetter(name));

const BlockedAvailability: React.FC = () => {
  const [selectedBlockedBooking, setSelectedBlockedBooking] = useState<
    string[]
  >([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState<boolean>(false);
  const [blockedBookingList, setBlockedBookingList] = useState<string[]>(
    blockedBookingNameList
  );

  const dispatch = useAppDispatch();
  const { selectedAvailabilitySettingDetail } =
    useAppSelector(availabilitySettings);

  const postBlockedBooking = async (blockedBookingArray: string[]) => {
    try {
      const response = await blockedBookingAvailabilitySettingApi(
        {
          block_booking: blockedBookingArray.map((item) =>
            item.toLocaleUpperCase()
          ),
        },
        selectedAvailabilitySettingDetail?.id
      );
      console.log("response", response);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    // Remove duplicates from newValue
    const uniqueNewValue = [...new Set(newValue)];

    if (uniqueNewValue.includes("All")) {
      // Select all values except "All"
      const allValuesExceptAll = blockedBookingNameList.filter(
        (value) => value !== "All"
      );
      setBlockedBookingList(blockedBookingNameList);
      setSelectedBlockedBooking(allValuesExceptAll);
      postBlockedBooking(allValuesExceptAll);
      dispatch(
        setBlockedBookingAvailabilitySetting({
          blockedRoles: allValuesExceptAll,
          availabilitySettingId: selectedAvailabilitySettingDetail?.id,
        })
      );
      return;
    }
    // Determine if an item was added or removed
    const previousSelection = selectedBlockedBooking;
    const added = uniqueNewValue.find(
      (item) => !previousSelection.includes(item)
    );
    const removed = previousSelection.find(
      (item) => !uniqueNewValue.includes(item)
    );

    if (added) {
      // Remove the added item from the available options
      setBlockedBookingList(
        blockedBookingList.filter((item) => item !== added)
      );
    }
    if (removed) {
      // Add the removed item back to available options
      setBlockedBookingList([...new Set([...blockedBookingList, removed])]);
    }
    setSelectedBlockedBooking(uniqueNewValue);
    postBlockedBooking(uniqueNewValue);
    dispatch(
      setBlockedBookingAvailabilitySetting({
        blockedRoles: uniqueNewValue,
        availabilitySettingId: selectedAvailabilitySettingDetail?.id,
      })
    );
  };

  const handleOpenDropdown = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSuggestionsOpen(!suggestionsOpen);
  };

  useEffect(() => {
    if (selectedAvailabilitySettingDetail?.blocked_roles) {
      setSelectedBlockedBooking(
        selectedAvailabilitySettingDetail?.blocked_roles?.map((item) =>
          capitalizeFirstLetter(item)
        )
      );
    }
  }, [selectedAvailabilitySettingDetail?.blocked_roles]);

  return (
    <Grid item sx={style.blockBookingForContainer}>
      <FormControl fullWidth>
        <Autocomplete
          multiple
          clearIcon={false}
          popupIcon={null}
          id="block-booking-autocomplete"
          options={blockedBookingList}
          open={suggestionsOpen}
          onOpen={() => setSuggestionsOpen(true)}
          onClose={() => setSuggestionsOpen(false)}
          value={selectedBlockedBooking}
          onChange={handleChange}
          disablePortal
          sx={style.autocompleteContainer}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Block Booking for"
              placeholder="Select"
              onClick={() => setSuggestionsOpen(true)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={style.selectBlockBookingForContainer}
                    onClick={handleOpenDropdown}
                  >
                    <Typography
                      component={"p"}
                      sx={style.selectBlockBookingFor}
                    >
                      Select
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                sx={style.blockBookingForChip}
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </FormControl>
    </Grid>
  );
};

export default BlockedAvailability;
